import { computed, IComputedValue } from 'mobx';
import { useObserver } from 'mobx-react';
import { useEffect } from 'react';
import { intl, translate } from '../../../../../common/intl';
import { DocumentRevisionStatus } from '../../../../../state/ducks/documentRevisions/types';
import { OptionType } from '../../../../components/forms/fields/Autocomplete/types';
import FBAutocompleteAsyncState from '../../../FBAutocompleteAsync/FBAutocompleteAsync.state';
import FBAutocompleteAsyncStore from '../../../FBAutocompleteAsync/FBAutocompleteAsync.store';
import { FBAutocompleteAsyncOption } from '../../../FBAutocompleteAsync/FBAutocompleteAsync.types';
import { FB } from '../../../helpers';
import { MaterialOption } from '../types';

interface MaterialFetchData {
  materialSelectOptions: OptionType[]
  allowedEditOptions: OptionType[]
}

const EMPTY_MATERIAL_OPTIONS: MaterialFetchData = {
  materialSelectOptions: [],
  allowedEditOptions: [],
};

const materialOptionsCache = new WeakMap<Map<string, MaterialOption>, Map<string, IComputedValue<MaterialFetchData>>>();

const mapToAutocompleteOption = (material: MaterialOption): OptionType => ({
  label: translate('common.select.revision', {
    docId: material.document.docId,
    displayRevision: material.displayRevision,
    name: material.name,
  }),
  value: material.id,
});

const getMaterialOptions = (
  materialMap: Map<string, MaterialOption> | undefined,
  isEBProcedure: boolean,
): MaterialFetchData => {
  if (!materialMap) {
    return EMPTY_MATERIAL_OPTIONS;
  }

  let variants = materialOptionsCache.get(materialMap);
  if (!variants) {
    variants = new Map();
    materialOptionsCache.set(materialMap, variants);
  }
  const variantKey = `${intl.locale}:${isEBProcedure ? 'eb' : 'released'}`;
  let options = variants.get(variantKey);
  if (!options) {
    options = computed(() => {
      const editAllowedStatuses = isEBProcedure
        ? [DocumentRevisionStatus.Released, DocumentRevisionStatus.Draft]
        : [DocumentRevisionStatus.Released];
      const materials = Array.from(materialMap.values());
      return {
        materialSelectOptions: materials.map(mapToAutocompleteOption),
        allowedEditOptions: materials
          .filter((material) => editAllowedStatuses.includes(material.status))
          .map(mapToAutocompleteOption),
      };
    });
    variants.set(variantKey, options);
  }
  return options.get();
};

export const useFetchData = ({ isEBProcedure }) => {
  const materialState = FB.useRef<FBAutocompleteAsyncState>(FBAutocompleteAsyncState, {
    optionId: FBAutocompleteAsyncOption.materials,
  });

  useEffect(() => {
    materialState.load();
  }, [materialState]);

  return useObserver(() => getMaterialOptions(
    FBAutocompleteAsyncStore.data.get(FBAutocompleteAsyncOption.materials) as Map<string, MaterialOption> | undefined,
    isEBProcedure,
  ));
};
